import React, { useEffect, useState, useMemo } from 'react';
import { fetchTournamentPointsMonthly, fetchTournamentPointsAllTime } from '../../api/tournament';
import { getMonthlyLeaderboardMonths, readableMonthYear } from '../../helpers/dateHelpers';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import Modal from '../../components/Modal';

const LEAGUE_NAMES = {
    1: 'Low-A',
    2: 'Single-A',
    3: 'High-A',
    4: 'AA',
    5: 'AAA',
    6: 'The Show',
};

const PLACEMENT_LABELS = {
    1: '1st',
    2: '2nd',
    3: '3rd',
};

const RANK_CLASS = ['rank-gold', 'rank-silver', 'rank-bronze'];

const buildFinishBreakdown = (finishes) => {
    const byLeague = new Map();
    for (const f of finishes) {
        if (!f.points) continue;
        const league = f.league;
        if (!byLeague.has(league)) byLeague.set(league, { totalPoints: 0, placements: {} });
        const entry = byLeague.get(league);
        entry.totalPoints += f.points;
        const p = f.placement;
        entry.placements[p] = (entry.placements[p] || 0) + 1;
    }
    return [...byLeague.entries()]
        .sort(([a], [b]) => b - a)
        .map(([league, data]) => ({ league, ...data }));
};

const TournamentPointsPage = () => {
    const [mode, setMode] = useState('monthly');
    const [monthYear, setMonthYear] = useState(() => getMonthlyLeaderboardMonths()[0]);
    const [sections, setSections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAllMonths, setShowAllMonths] = useState(false);
    const [finishesModal, setFinishesModal] = useState(null);

    const months = useMemo(() => getMonthlyLeaderboardMonths(), []);
    const displayedMonths = showAllMonths ? months : months.slice(0, 3);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const data = mode === 'alltime'
                    ? await fetchTournamentPointsAllTime()
                    : await fetchTournamentPointsMonthly(monthYear);
                setSections(data);
            } catch (error) {
                toast.error('Error fetching leaderboard: ' + error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [mode, monthYear]);

    const getPoints = (row) => row.totalPoints ?? row.points;
    const getUsername = (row) => row.User?.username ?? row.username ?? '—';
    const timeLabel = mode === 'alltime' ? 'All Time' : readableMonthYear(monthYear);

    return (
        <div className="leader-container">
            <div className="header">
                <h1>{timeLabel} Tournament Points</h1>
            </div>

            <div className="month-buttons">
                <button
                    className={`month-btn ${mode === 'alltime' ? 'active' : ''}`}
                    onClick={() => setMode('alltime')}
                >
                    All Time
                </button>
                {displayedMonths.map(m => (
                    <button
                        key={m}
                        className={`month-btn ${mode === 'monthly' && monthYear === m ? 'active' : ''}`}
                        onClick={() => { setMode('monthly'); setMonthYear(m); }}
                    >
                        {readableMonthYear(m)}
                    </button>
                ))}
                {months.length > 3 && (
                    <button onClick={() => setShowAllMonths(!showAllMonths)} className="toggle-months-btn">
                        {showAllMonths ? '(show less)' : '(show more)'}
                    </button>
                )}
            </div>

            {loading ? (
                <LoadingSpinner />
            ) : sections.length === 0 ? (
                <div className="no-data-message">No tournament points recorded yet.</div>
            ) : (
                <div className="leaderboard-grid">
                    {sections.map(section => (
                        <div key={section.tier} className="stat-category">
                            <h3>{section.label} <span className="tp-level-range">Lvl {section.levelRange}</span></h3>
                            <ul>
                                {section.rows.map((row, i) => (
                                    <li key={row.userID} className="tp-row">
                                        <span className={`tp-rank ${RANK_CLASS[i] || ''}`}>{row.rank}</span>
                                        <Link to={`/u/${getUsername(row)}`}>{getUsername(row)}</Link>
                                        {row.meta?.finishes ? (
                                            <button
                                                className="tp-points-btn"
                                                onClick={() => setFinishesModal({ username: getUsername(row), finishes: row.meta.finishes, points: getPoints(row) })}
                                            >
                                                {getPoints(row)} pts
                                            </button>
                                        ) : (
                                            <span className="tp-points">{getPoints(row)} pts</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            )}

            {finishesModal && (
                <Modal onClose={() => setFinishesModal(null)} title="Tournament Finishes">
                    <FinishesBreakdown data={finishesModal} />
                </Modal>
            )}
        </div>
    );
};

const FinishesBreakdown = ({ data }) => {
    const breakdown = useMemo(() => buildFinishBreakdown(data.finishes), [data.finishes]);

    return (
        <div className="tp-modal">
            <div className="tp-modal-header">
                <h2>{data.username}</h2>
                <div className="tp-modal-total">{data.points} pts</div>
            </div>

            <div className="tp-modal-leagues">
                {breakdown.map(({ league, totalPoints, placements }) => (
                    <div key={league} className="tp-league-card">
                        <div className="tp-league-header">
                            <span className="tp-league-name">{LEAGUE_NAMES[league] || `League ${league}`}</span>
                            <span className="tp-league-pts">{totalPoints} pts</span>
                        </div>
                        <div className="tp-placement-row">
                            {Object.entries(placements)
                                .sort(([a], [b]) => Number(a) - Number(b))
                                .map(([placement, count]) => (
                                    <div key={placement} className={`tp-placement tp-placement-${placement}`}>
                                        <span className="tp-placement-pos">{PLACEMENT_LABELS[placement] || `${placement}th`}</span>
                                        <span className="tp-placement-count">{count}x</span>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default TournamentPointsPage;
